import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, money, PAYMENT_RU, phoneFmt, todayIso } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, Status, useToast } from '../ui';

type Order = {
  id: string;
  number: string;
  status: string;
  deliveryDate: string;
  timeFrom: string | null;
  timeTo: string | null;
  totalKop: string;
  plannedGive: number;
  plannedTake: number;
  paymentMethod: string;
  comment: string | null;
  client: { id: string; name: string; phone: string; cachedBalanceKop: string; cachedBottles: number };
  address: { street: string; house: string; flat: string | null; comment: string | null };
  routeStop: { route: { id: string; status: string } } | null;
  items: Array<{ name: string; qty: number }>;
};

export function Orders() {
  const [date, setDate] = useState(todayIso());
  const [onlyUnrouted, setOnlyUnrouted] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [driverId, setDriverId] = useState('');
  const toast = useToast();
  const nav = useNavigate();

  const url = `/orders?date=${date}&limit=300${onlyUnrouted ? '&unrouted=true' : ''}`;
  const { data, error, loading, reload } = useApi<{ items: Order[]; total: number }>(url);
  const drivers = useApi<Array<{ id: string; name: string; role: string }>>('/users');

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  /** Заказы, которые ещё можно поставить в маршрут: свободные и не отработанные. */
  const free = (data?.items ?? []).filter((o) => !o.routeStop && o.status === 'NEW');

  const buildRoute = async () => {
    if (!selected.size) return;
    const count = selected.size;
    try {
      await api('/routes', { body: { date, driverId: driverId || null, orderIds: [...selected] } });
      toast.show(`Маршрут собран: ${count} точек. Порядок объезда — на «Доске»`, 4000);
      setSelected(new Set());
      setDriverId('');
      await reload();
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Маршрут не собрался', 5000);
    }
  };

  return (
    <>
      <div className="page-head">
        <h1>Заказы</h1>
        <button className="ghost" onClick={() => nav(`/board?date=${date}`)}>
          Открыть доску развозки →
        </button>
      </div>

      <div className="card">
        <div className="row">
          <div className="field" style={{ flex: '0 0 170px', marginBottom: 0 }}>
            <label>Дата доставки</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-end', margin: '0 0 8px' }}>
            <input
              type="checkbox"
              style={{ width: 16 }}
              checked={onlyUnrouted}
              onChange={(e) => setOnlyUnrouted(e.target.checked)}
            />
            только не в маршруте
          </label>
          <div style={{ flex: 1 }} />
          <div style={{ alignSelf: 'flex-end', margin: '0 0 8px' }} className="muted">
            {free.length
              ? `Свободных заказов: ${free.length}. Отметьте нужные — снизу появится сборка маршрута.`
              : 'Свободных заказов нет: все либо в маршруте, либо отработаны.'}
          </div>
        </div>
      </div>

      {/* Панель сборки маршрута. Висит внизу экрана, пока есть отметки: раньше
          она пряталась выпадающим списком в шапке, и её не находили. */}
      {selected.size ? (
        <div className="actionbar">
          <div>
            <b>Выбрано заказов: {selected.size}</b>
            <div className="muted">везём {free.filter((o) => selected.has(o.id)).reduce((s, o) => s + o.plannedGive, 0)} бутылей</div>
          </div>
          <select value={driverId} style={{ width: 220 }} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">Водитель: назначу позже</option>
            {(drivers.data ?? []).filter((u) => u.role === 'DRIVER').map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <button className="primary" onClick={() => void buildRoute()}>
            Собрать маршрут
          </button>
          <button className="ghost" onClick={() => setSelected(new Set())}>
            Снять отметки
          </button>
        </div>
      ) : null}

      <div className="card">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorBox error={error} />
        ) : !data?.items.length ? (
          <Empty>На {date} заказов нет. Их создаёт подписка или оператор в карточке клиента.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    style={{ width: 16 }}
                    title="Отметить все свободные"
                    disabled={!free.length}
                    checked={!!free.length && free.every((o) => selected.has(o.id))}
                    onChange={(e) => setSelected(e.target.checked ? new Set(free.map((o) => o.id)) : new Set())}
                  />
                </th>
                <th>Номер</th>
                <th>Клиент</th>
                <th>Адрес</th>
                <th>Окно</th>
                <th>Состав</th>
                <th className="num">Сумма</th>
                <th>Оплата</th>
                <th>Состояние</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id}>
                  <td>
                    {!o.routeStop && o.status === 'NEW' ? (
                      <input
                        type="checkbox"
                        style={{ width: 16 }}
                        checked={selected.has(o.id)}
                        onChange={() => toggle(o.id)}
                      />
                    ) : null}
                  </td>
                  <td className="mono">
                    <Link to={`/orders/${o.id}`}>{o.number}</Link>
                  </td>
                  <td>
                    <Link to={`/clients/${o.client.id}`}>{o.client.name}</Link>
                    <div className="muted mono" style={{ fontSize: 12 }}>{phoneFmt(o.client.phone)}</div>
                    {BigInt(o.client.cachedBalanceKop) < 0n ? (
                      <span className="badge red">долг {money(-BigInt(o.client.cachedBalanceKop))}</span>
                    ) : null}
                  </td>
                  <td>
                    {o.address.street}, {o.address.house}
                    {o.address.flat ? `, ${o.address.flat}` : ''}
                    {o.address.comment ? <div className="muted" style={{ fontSize: 12 }}>{o.address.comment}</div> : null}
                  </td>
                  <td className="muted">{o.timeFrom ? `${o.timeFrom}–${o.timeTo ?? ''}` : '—'}</td>
                  <td className="muted">
                    {o.items.map((i) => `${i.name} × ${i.qty}`).join(', ')}
                    <div style={{ fontSize: 12 }}>
                      везём {o.plannedGive}, забираем {o.plannedTake}
                    </div>
                  </td>
                  <td className="num">{money(o.totalKop)}</td>
                  <td className="muted">{PAYMENT_RU[o.paymentMethod] ?? o.paymentMethod}</td>
                  <td>
                    <Status value={o.status} />
                    {o.routeStop ? <div className="muted" style={{ fontSize: 12 }}>в маршруте</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {toast.node}
    </>
  );
}
